import { Injectable, inject } from '@angular/core';
import { CaseFormFieldEntity } from '@features/case-forms';
import { InvestigationFormFieldEntity } from '@features/investigation-forms';
import { LoggerProvider } from '../services/logger';
import type {
  FormField,
  FormFieldOption,
  FormFieldType,
  FormFieldVisibilityRule,
} from '../models/form-field.model';

/**
 * Shared Form Field Mapper
 * Maps CaseFormFieldEntity or InvestigationFormFieldEntity to FormField
 * Used by page-data services and use cases for form field rendering
 */
@Injectable({ providedIn: 'root' })
export class FormFieldMapper {
  private readonly _logger = inject(LoggerProvider).createScoped('FormFieldMapper');

  /**
   * Maps CaseFormFieldEntity[] or InvestigationFormFieldEntity[] to FormField[]
   * Sorts fields by sortOrder after mapping
   */
  public mapToFormFields(
    fields: CaseFormFieldEntity[] | InvestigationFormFieldEntity[]
  ): FormField[] {
    const mappedFields = fields.map((field) => ({
      id: field.id.toString(),
      label: field.label,
      type: this._mapFieldType(field.fieldType),
      placeholder: field.placeholder,
      helpText: field.helperText,
      required: field.isRequired,
      options: this._extractOptions(field),
      maxLength: this._extractMaxLength(field),
      defaultValue: '',
      hidden: field.isHiddenByDefault ?? false,
      isVisibleToRequester: field.isVisibleToRequester ?? true,
      order: field.sortOrder,
      hiddenByDefault: field.isHiddenByDefault ?? false,
      visibilityRules: this._mapVisibilityRules(field),
    }));

    return mappedFields.sort((a, b) => a.order - b.order);
  }

  /**
   * Maps field type string to FormFieldType
   */
  private _mapFieldType(fieldType: string): FormFieldType {
    const typeMap: Record<string, FormFieldType> = {
      text: 'text',
      textarea: 'textarea',
      email: 'email',
      number: 'number',
      currency: 'number', // Currency fields use number input with validation
      date: 'date',
      time: 'text', // Time fields use text input with time picker (if supported) or validation
      select: 'select',
      dropdown: 'select',
      checkbox: 'checkbox',
      checkboxes: 'checkbox',
      radio: 'radio',
      radio_buttons: 'radio',
      file: 'file',
      file_upload: 'file',
      yes_no: 'yes-no',
    };

    return typeMap[fieldType] ?? 'text';
  }

  /**
   * Extracts options from field constraints for select/radio/checkbox fields
   * Returns empty array if no options are found or parsing fails
   */
  private _extractOptions(
    field: CaseFormFieldEntity | InvestigationFormFieldEntity
  ): FormFieldOption[] {
    if (field?.constraints?.length === 0) {
      return [];
    }

    // Find the options constraint
    const optionsConstraint = field?.constraints?.find((c) => c.constraintKey === 'options');

    if (!optionsConstraint?.value) {
      return [];
    }

    try {
      // Parse the JSON string value
      const parsed = JSON.parse(optionsConstraint.value);

      // Validate parsed structure
      if (!Array.isArray(parsed)) {
        this._logger.warn('Invalid options format: expected array');
        return [];
      }

      // Sanitize each option to prevent prototype pollution
      return parsed
        .filter((opt) => opt && typeof opt === 'object' && !Array.isArray(opt))
        .map((opt: FormFieldOption) => ({
          value: String(opt.value ?? ''),
          label: String(opt.label ?? ''),
        }));
    } catch (error) {
      this._logger.error('Failed to parse field options', error);
      return [];
    }
  }

  /**
   * Extracts maxLength constraint from field constraints
   * Returns undefined if not found or invalid
   * Uses 'max_length' (snake_case) - backend standard
   */
  private _extractMaxLength(
    field: CaseFormFieldEntity | InvestigationFormFieldEntity
  ): number | undefined {
    if (!field.constraints || field.constraints.length === 0) {
      return undefined;
    }

    const maxLengthConstraint = field.constraints.find((c) => c.constraintKey === 'max_length');

    if (!maxLengthConstraint?.value) {
      return undefined;
    }

    const maxLength = Number(maxLengthConstraint.value);
    return Number.isFinite(maxLength) && maxLength > 0 ? maxLength : undefined;
  }

  /**
   * Maps visibility rule from field entity to FormFieldVisibilityRule[]
   * Returns empty array if no visibility rule exists
   */
  private _mapVisibilityRules(
    field: CaseFormFieldEntity | InvestigationFormFieldEntity
  ): FormFieldVisibilityRule[] {
    if (!field.visibilityRule) {
      return [];
    }

    return [
      {
        id: '1',
        sourceFieldId: field.visibilityRule.sourceFieldId,
        operator: field.visibilityRule.operator,
        value: field.visibilityRule.value,
        action: field.visibilityRule.show ? 'show' : 'hide',
      },
    ];
  }
}
